/**
 * The finite table-presentation document edited by Table Manager (#1982).
 *
 * It contains presentation and composition only: no SQL, React/HTML, route,
 * callback or authority predicate. `board` is the governed storage key already
 * registered by core.view_kind; a host may label that lens "Kanban".
 */
export const TABLE_PRESENTATIONS = ['grid', 'calendar', 'board', 'cards'] as const
export type TablePresentation = typeof TABLE_PRESENTATIONS[number]
/**
 * Whether the governed COMMAND BUTTONS sit beside the collection's mark
 * (#2375, ADR F-84 §3). Selection actions are inline from the first selected
 * row whatever this says, so there is no longer an "appears on selection"
 * value to express.
 */
export type TableCommandBarDefault = 'shown' | 'hidden'

/**
 * The legacy stored value. `auto` used to mean "a floating bar appears on
 * selection"; that bar is deleted, and a stored `auto` is READ as `shown`
 * for ONE compatibility release (the core trigger canonicalises every authored
 * write, core 0.403.0), then removed through the governed migration path. No
 * new write may persist it.
 */
export type LegacyTableCommandBar = TableCommandBarDefault | 'auto'

/** The one-release read alias, and the notice every reader reports for it. */
export const DEPRECATED_COMMAND_BAR_ALIAS = 'auto' as const
export const COMMAND_BAR_DEPRECATION =
  'commandBar "auto" is a deprecated alias of "shown" (ADR F-84); it is read as "shown" for one compatibility release'

/** A stored document's command bar, canonical. Anything but `hidden` reads `shown`. */
export function canonicalCommandBar(value: unknown): TableCommandBarDefault {
  return value === 'hidden' ? 'hidden' : 'shown'
}

/** The document with a legacy `auto` command bar read as `shown`; otherwise itself. */
export function canonicalTablePresentation<T>(value: T): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value
  const row = value as Record<string, unknown>
  return row.commandBar === DEPRECATED_COMMAND_BAR_ALIAS ? { ...row, commandBar: 'shown' } as T : value
}

/** Non-fatal notices for a stored document that uses a deprecated alias. */
export function tablePresentationDeprecations(value: unknown): string[] {
  return value && typeof value === 'object' && !Array.isArray(value)
    && (value as Record<string, unknown>).commandBar === DEPRECATED_COMMAND_BAR_ALIAS
    ? [COMMAND_BAR_DEPRECATION] : []
}

export interface TableQuickCreateSpec {
  /** Stable governed core.form reference/name; the resolver binds it. */
  form?: string
  /** Important fields in visual order. Required-on-create fields are added by validation/resolution. */
  fields: string[]
}

export interface TableCardSpec {
  primary: string
  details?: string[]
  badge?: string
  accent?: string
  icon?: string
  /** Stable governed action ids/capabilities, never client callbacks. */
  actions?: string[]
}

/* ── the collection's COMMANDS (#2375, version 5) ───────────────────────────
 *
 * The buttons beside a collection's mark — "New", "Link existing", a module's
 * collection-level verb — as DATA, so a deployment can choose, order and word
 * them without a component edit.
 *
 * ── what it is NOT ──────────────────────────────────────────────────────────
 * A command grants nothing. Presentation may SELECT, ARRANGE and LABEL a
 * command that is already governed; it never makes one. So `action` names
 * either one of the two BUILT-IN seams every collection host owns
 * (`BUILTIN_COMMAND_ACTIONS`), or the `tool_name` of a `core.entity_action`
 * binding for THIS entity with `cardinality = 'none'` — a collection-level
 * verb, whose registered MCP tool remains the invocation and authorization
 * contract. `core.enforce_table_presentation_references` refuses anything else
 * at the database boundary, and `resolveTableCommands` below drops, by name,
 * whatever this host is not authorised to run at render time.
 *
 * ── absent is a legitimate state ────────────────────────────────────────────
 * With no `commands` block a host DERIVES its commands from what it can do —
 * `New` when quick create is available, `Link existing` when linking is — which
 * is exactly what every grid drew before this block existed. An explicit empty
 * list is a different answer: "this collection offers no commands".
 */
export type TableCommandKind = 'primary' | 'secondary'

export interface TableCommand {
  /** Stable identity within the list; never shown to a person. */
  id: string
  /** The words on the button. */
  label: string
  /** At most one `primary`, and it is the FIRST entry. */
  kind: TableCommandKind
  /** A built-in seam, or a governed collection-level `core.entity_action` tool name. */
  action: string
}

/** The seams every collection host owns, which need no `core.entity_action` row. */
export const BUILTIN_COMMAND_ACTIONS = ['quickCreate', 'linkExisting'] as const
export type BuiltinCommandAction = typeof BUILTIN_COMMAND_ACTIONS[number]

export const TABLE_COMMAND_KINDS = ['primary', 'secondary'] as const

export const isBuiltinCommandAction = (action: unknown): action is BuiltinCommandAction =>
  typeof action === 'string' && (BUILTIN_COMMAND_ACTIONS as readonly string[]).includes(action)

export interface TablePresentationDocument {
  /**
   * 1 is #1987's grammar. 2 adds `map` and `related` (#2276). 3 adds the
   * whole-record relationship `workspace` (#2324). 4 adds the relationship
   * `picker` rail (#2351). 5 adds the collection's `commands` (#2375). Each is
   * REQUIRED the moment its block appears — deliberately, so a reader of the
   * older grammar can tell it is not seeing everything rather than silently
   * ignoring half the document.
   *
   * The version is a FLOOR and the grammar GROWS: a version-5 document may
   * carry every earlier block, and a version-1 document stays valid and keeps
   * resolving. Nothing below reads `version` to decide what to render.
   */
  version: 1 | 2 | 3 | 4 | 5
  availablePresentations: TablePresentation[]
  defaultPresentation: TablePresentation
  commandBar: TableCommandBarDefault
  quickCreate?: TableQuickCreateSpec
  card?: TableCardSpec
  /** Where this entity's coordinates live, when it has any (version 2). */
  map?: MapRecipe
  /** What a relationship workspace offers over this entity (version 2). */
  related?: RelatedOffer
  /** Which of this entity's own form sections a Peek preview shows (version 2). */
  peek?: PeekRecipe
  /** Which date column a Timeline buckets, and what it measures (version 2). */
  timeline?: TimelineRecipe
  /** How this record's WHOLE relationship workspace reads (version 3). */
  workspace?: WorkspaceContract
  /** How the relationship-authoring rail groups and describes this record's slots (version 4). */
  picker?: PickerPresentation
  /** The collection's commands, in the order they are drawn (version 5). Absent ⇒ derived. */
  commands?: readonly TableCommand[]
}

/**
 * The relationship-picker rail's presentation — grouping and wording, and
 * nothing else (#2351).
 *
 * ── what it is NOT ─────────────────────────────────────────────────────────
 * It carries no eligibility, no cardinality, no authority and no write
 * capability. Those are server facts the picker already reads off the
 * relationship type and the write contract (`write_mode`/`write_operations`,
 * `core.mutate_relationship`), and a presentation document that could withhold
 * a relationship would be a second, weaker access control.
 *
 * ── why it lives HERE and not on `core.relationship_type` ──────────────────
 * Grouping is a judgement about how one record's authoring surface reads, and
 * it differs per source entity: `located_at` belongs under "Locations" on an
 * organisation and may belong beside the person's own contact facts on a
 * person. A column on the relationship type would force one answer for every
 * record type that uses it, and — the deciding argument — it would be a module
 * preset a deployment could not override. A `core.experience` row of kind
 * `table` is already layered (system → solution → tenant → persona), already
 * validated by `core.enforce_table_presentation` as the trust boundary, and
 * already travels through ALM, which is rule 44's two tiers with nothing new
 * built.
 *
 * ── absent is a legitimate state ───────────────────────────────────────────
 * With no block the rail renders every slot the metamodel derives, in the
 * order `deriveRelationSlots` already produces, under one ungrouped heading.
 * That is why no Moonbase bucket name appears in any component: the groups in
 * the #2351 mockup ship as preset rows, and a deployment that wants different
 * words changes data.
 */
export interface PickerGroup {
  /** Stable key referenced by `slots[*].group`; never shown to a person. */
  key: string
  /** The words on the rail heading. */
  label: string
  /** Ascending. Equal or absent sorts fall back to label, then key. */
  sort?: number
}

export interface PickerSlotPresentation {
  /**
   * The far entity this slot reaches — DECLARED, never parsed out of the slot
   * key. `related.perSlot` establishes the rule and the reason: the key
   * contains the entity, and reading it back out makes a renamed relationship
   * type a silent presentation change.
   */
  target: string
  /** A `groups[*].key`. An unknown key is refused by name, never dropped. */
  group?: string
  /** The one-line purpose under the label. Absent ⇒ the rail shows the target entity alone. */
  description?: string
  /**
   * A governed icon key, rendered by the one shared `EntityIcon` renderer.
   * Absent ⇒ the TARGET ENTITY's own registered `icon_key`, which is already
   * governed data — so the common case declares no icon at all.
   */
  icon?: string
}

export interface PickerPresentation {
  groups: PickerGroup[]
  /** Keyed by relationship slot identity (`<relType>:<direction>:<otherEntity>`). */
  slots?: Record<string, PickerSlotPresentation>
}

/**
 * Validate a picker block. Mirrors `core.validate_picker_presentation` clause
 * for clause: an author must not be told yes here and no at the trigger.
 */
export function validatePickerPresentation(value: unknown, path = 'picker'): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [`${path} must be an object`]
  const row = value as Record<string, unknown>
  const issues: string[] = []
  rejectUnknown(row, ['groups', 'slots'], path, issues)

  const groupKeys: string[] = []
  if (!Array.isArray(row.groups)) {
    issues.push(`${path}.groups must be an array of groups`)
  } else {
    if (row.groups.length === 0) issues.push(`${path}.groups must declare at least one group`)
    row.groups.forEach((group, index) => {
      const at = `${path}.groups[${index}]`
      if (!group || typeof group !== 'object' || Array.isArray(group)) {
        issues.push(`${at} must be an object`)
        return
      }
      const declared = group as Record<string, unknown>
      rejectUnknown(declared, ['key', 'label', 'sort'], at, issues)
      if (!named(declared.key)) issues.push(`${at} must carry a non-empty key`)
      else groupKeys.push(declared.key)
      if (!named(declared.label)) issues.push(`${at} must carry a non-empty label`)
      if (declared.sort != null && !Number.isFinite(declared.sort)) {
        issues.push(`${at}.sort must be a number`)
      }
    })
    for (const key of new Set(duplicates(groupKeys))) issues.push(`${path} declares duplicate group ${key}`)
  }

  if (row.slots != null) {
    if (typeof row.slots !== 'object' || Array.isArray(row.slots)) {
      issues.push(`${path}.slots must be an object keyed by relationship slot identity`)
    } else {
      const known = new Set(groupKeys)
      for (const [slot, declared] of Object.entries(row.slots as Record<string, unknown>)) {
        const at = `${path}.slots.${slot}`
        if (slot.trim() === '') issues.push(`${path}.slots has an empty slot identity`)
        if (!declared || typeof declared !== 'object' || Array.isArray(declared)) {
          issues.push(`${at} must be an object`)
          continue
        }
        const child = declared as Record<string, unknown>
        rejectUnknown(child, ['target', 'group', 'description', 'icon'], at, issues)
        if (!named(child.target)) issues.push(`${at} must name the target entity it applies to`)
        // Refused BY NAME. A slot pointing at a group nobody declared would
        // otherwise land in whichever bucket the renderer happened to use for
        // the leftovers, which is a governed document disagreeing with itself
        // in silence — the rule `core.validate_peek_recipe` sets for an unknown
        // section id.
        if (child.group != null) {
          if (!named(child.group)) issues.push(`${at}.group must be a non-empty group key`)
          else if (!known.has(child.group)) issues.push(`${at} names group ${child.group}, which ${path} does not declare`)
        }
        for (const property of ['description', 'icon'] as const) {
          if (child[property] != null && !named(child[property])) {
            issues.push(`${at}.${property} must be a non-empty string`)
          }
        }
      }
    }
  }
  return issues
}

/**
 * The rail's groups, in the order they are drawn — and the leftover heading.
 *
 * Kept next to the validator so the ordering rule is stated once: `sort`
 * ascending, then label, then key. Slots with no declared group, and every slot
 * on a deployment that declares no picker block at all, fall into the
 * UNGROUPED bucket, which a host renders without a heading rather than under an
 * invented one.
 */
export const PICKER_UNGROUPED = ''

export function pickerGroupOrder(picker: PickerPresentation | null | undefined): PickerGroup[] {
  const groups = [...(picker?.groups ?? [])]
  return groups.sort((a, b) =>
    (a.sort ?? Number.MAX_SAFE_INTEGER) - (b.sort ?? Number.MAX_SAFE_INTEGER)
    || a.label.localeCompare(b.label)
    || a.key.localeCompare(b.key))
}

/**
 * The declared presentation of ONE slot, or null.
 *
 * `target` is checked against what the host derived: a declaration whose target
 * disagrees with the slot the metamodel produced is a stale document, and
 * honouring its wording would describe the wrong entity. It is ignored rather
 * than obeyed, and the caller keeps the derived reading.
 */
export function pickerSlotPresentation(
  picker: PickerPresentation | null | undefined,
  slot: string,
  target: string,
): PickerSlotPresentation | null {
  const declared = picker?.slots?.[slot]
  if (!declared) return null
  return declared.target === target ? declared : null
}

export interface ResolvedTablePresentation {
  document: TablePresentationDocument
  active: TablePresentation
  status: 'resolved' | 'fallback'
  /** Must be surfaced by an authoring/host boundary; never render a blank lens. */
  issues: string[]
  /**
   * NON-FATAL: the stored document used a deprecated alias that was read
   * canonically (ADR F-84). Reported, never a fallback — a legacy value that
   * still means something must not blank a surface.
   */
  deprecations?: string[]
}

const PRESENTATIONS = new Set<string>(TABLE_PRESENTATIONS)
/* `auto` is ACCEPTED as the one-release read alias (see LegacyTableCommandBar);
   the resolvers below read it as `shown` and report a deprecation. */
const COMMAND_BAR = new Set<string>([DEPRECATED_COMMAND_BAR_ALIAS, 'shown', 'hidden'])
const keys = (value: unknown): string[] => Array.isArray(value)
  ? value.filter((item): item is string => typeof item === 'string')
  : []
const duplicates = (values: string[]): string[] => values.filter((value, index) => values.indexOf(value) !== index)
const named = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0
const rejectUnknown = (row: Record<string, unknown>, allowed: readonly string[], path: string, issues: string[]) => {
  for (const key of Object.keys(row)) if (!allowed.includes(key)) issues.push(`${path} contains unsupported property ${key}`)
}
/** The grammar versions this build reads. The floor checks below compare against them. */
export const TABLE_PRESENTATION_VERSIONS = [1, 2, 3, 4, 5] as const
const atLeast = (version: unknown, floor: number): boolean =>
  typeof version === 'number' && (TABLE_PRESENTATION_VERSIONS as readonly number[]).includes(version) && version >= floor

export const DEFAULT_TABLE_PRESENTATION: TablePresentationDocument = {
  version: 1,
  availablePresentations: ['grid'],
  defaultPresentation: 'grid',
  commandBar: 'shown',
}

export function validateTablePresentation(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return ['table presentation must be an object']
  const row = value as Record<string, unknown>
  const issues: string[] = []
  rejectUnknown(row, ['version', 'availablePresentations', 'defaultPresentation', 'commandBar', 'quickCreate', 'card', 'map', 'related', 'peek', 'timeline', 'workspace', 'picker', 'commands'], 'table presentation', issues)
  if (row.version !== 1 && row.version !== 2 && row.version !== 3 && row.version !== 4 && row.version !== 5) {
    issues.push('table presentation version must be 1, 2, 3, 4 or 5')
  }
  // Each block raises the floor to its own version. Mirrors
  // core.validate_presentation_extensions exactly: an author must not be told
  // yes here and no at the trigger. It is a FLOOR and not an equality — the
  // grammar GROWS rather than forking, so a version-3 document may still carry
  // every version-2 block, `timeline` included.
  if ((row.map != null || row.related != null || row.peek != null || row.timeline != null)
      && !atLeast(row.version, 2)) {
    issues.push('a map recipe or a related offer makes this a version 2 table presentation')
  }
  if (row.workspace != null && !atLeast(row.version, 3)) {
    issues.push('a relationship-workspace contract makes this a version 3 table presentation')
  }
  // Version 4 on its own, and the same FLOOR rule as its two predecessors: a
  // version-4 document may still carry the workspace contract and every
  // version-2 block, because the grammar grows rather than forking.
  if (row.picker != null && !atLeast(row.version, 4)) {
    issues.push('a relationship-picker presentation makes this a version 4 table presentation')
  }
  // Version 5 (#2375): the collection's commands. Same floor rule — a
  // version-5 document may carry the picker, the workspace and every
  // version-2 block.
  if (row.commands != null && !atLeast(row.version, 5)) {
    issues.push('a command list makes this a version 5 table presentation')
  }
  if (row.commands != null) issues.push(...validateTableCommands(row.commands, row))
  if (row.map != null) issues.push(...validateMapRecipe(row.map))
  if (row.related != null) issues.push(...validateRelatedOffer(row.related))
  if (row.peek != null) issues.push(...validatePeekRecipe(row.peek))
  if (row.timeline != null) issues.push(...validateTimelineRecipe(row.timeline))
  if (row.workspace != null) issues.push(...validateWorkspaceContract(row.workspace))
  if (row.picker != null) issues.push(...validatePickerPresentation(row.picker))

  const available = keys(row.availablePresentations)
  if (available.length === 0) issues.push('availablePresentations must contain at least one presentation')
  if (Array.isArray(row.availablePresentations) && row.availablePresentations.some((key) => !named(key))) {
    issues.push('availablePresentations must contain only non-empty keys')
  }
  for (const key of available) {
    if (!PRESENTATIONS.has(key)) issues.push(`unsupported table presentation ${key}`)
  }
  for (const key of new Set(duplicates(available))) issues.push(`duplicate table presentation ${key}`)
  if (!named(row.defaultPresentation) || !PRESENTATIONS.has(row.defaultPresentation)) {
    issues.push(`unsupported default table presentation ${String(row.defaultPresentation ?? '')}`)
  } else if (!available.includes(row.defaultPresentation)) {
    issues.push(`default presentation ${row.defaultPresentation} is not available`)
  }
  if (!named(row.commandBar) || !COMMAND_BAR.has(row.commandBar)) {
    issues.push(`unsupported command-bar default ${String(row.commandBar ?? '')}`)
  }

  if (row.quickCreate != null) {
    if (typeof row.quickCreate !== 'object' || Array.isArray(row.quickCreate)) {
      issues.push('quickCreate must be an object')
    } else {
      const quick = row.quickCreate as Record<string, unknown>
      rejectUnknown(quick, ['form', 'fields'], 'quickCreate', issues)
      if (quick.form != null && !named(quick.form)) issues.push('quickCreate.form must be a non-empty reference')
      const fields = keys(quick.fields)
      if (!Array.isArray(quick.fields)) issues.push('quickCreate.fields must be an array')
      else if (fields.length === 0) issues.push('quickCreate.fields must contain at least one field')
      if (Array.isArray(quick.fields) && quick.fields.some((field) => !named(field))) issues.push('quickCreate fields must be non-empty keys')
      for (const field of new Set(duplicates(fields))) issues.push(`duplicate quick-create field ${field}`)
    }
  }

  if (row.card != null) {
    if (typeof row.card !== 'object' || Array.isArray(row.card)) {
      issues.push('card must be an object')
    } else {
      const card = row.card as Record<string, unknown>
      rejectUnknown(card, ['primary', 'details', 'badge', 'accent', 'icon', 'actions'], 'card', issues)
      if (!named(card.primary)) issues.push('card.primary must name a field')
      for (const property of ['details', 'actions'] as const) {
        if (card[property] != null && !Array.isArray(card[property])) issues.push(`card.${property} must be an array`)
        if (Array.isArray(card[property]) && card[property].some((item) => !named(item))) {
          issues.push(`card.${property} must contain non-empty keys`)
        }
      }
      for (const property of ['badge', 'accent', 'icon'] as const) {
        if (card[property] != null && !named(card[property])) issues.push(`card.${property} must name a field or governed token`)
      }
    }
  }
  if (available.includes('cards') && row.card == null) issues.push('cards presentation requires a card visualization')
  return issues
}

/**
 * Validate a command list. Mirrors the `commands` block of
 * `core.enforce_table_presentation` (shape) and the document-local half of
 * `core.enforce_table_presentation_references` clause for clause, in the same
 * words: an author must not be told yes here and no at the trigger.
 *
 * Deliberately NOT checked here: whether a non-built-in `action` is a governed
 * collection verb for this entity. The browser holds no `core.entity_action`
 * catalogue, and a client that guessed would disagree with the trigger that
 * knows — the split `validateMapRecipe` keeps for numeric coordinates. An
 * authoring surface that HAS the catalogue (the Table Manager) checks it from
 * the choices it was given; a host checks it at render with
 * `resolveTableCommands`.
 *
 * `document` is the enclosing table presentation, for the one rule that reads
 * it: a `quickCreate` command needs the document's own `quickCreate` block.
 */
export function validateTableCommands(value: unknown, document?: unknown): string[] {
  if (!Array.isArray(value)) return ['commands must be an array of commands']
  const issues: string[] = []
  const ids: string[] = []
  const primaries: number[] = []
  value.forEach((entry, index) => {
    const at = `commands[${index}]`
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      issues.push(`${at} must be an object`)
      return
    }
    const command = entry as Record<string, unknown>
    rejectUnknown(command, ['id', 'label', 'kind', 'action'], at, issues)
    if (!named(command.id)) issues.push(`${at} must carry a non-empty id`)
    else ids.push(command.id)
    if (!named(command.label)) issues.push(`${at} must carry a non-empty label`)
    if (!named(command.kind) || !(TABLE_COMMAND_KINDS as readonly string[]).includes(command.kind)) {
      issues.push(`${at} has an unsupported kind ${String(command.kind ?? '(absent)')}`)
    } else if (command.kind === 'primary') {
      primaries.push(index)
    }
    if (!named(command.action)) issues.push(`${at} must name an action`)
  })
  for (const id of new Set(duplicates(ids))) issues.push(`commands declares duplicate command ${id}`)
  // ONE primary, and it LEADS. A host draws the primary as the filled button
  // beside the mark and the rest after it; a primary in the middle of the list
  // would be drawn somewhere the author did not put it.
  if (primaries.length > 1) issues.push('commands declares more than one primary command')
  else if (primaries.length === 1 && primaries[0] !== 0) issues.push('the primary command must be the first command')
  // "New" opens the document's own quick-create form. Without that block there
  // is no form to open, so the button would be a promise nothing can keep.
  const doc = (document && typeof document === 'object' && !Array.isArray(document)
    ? document : {}) as Record<string, unknown>
  if (value.some((entry) => entry && typeof entry === 'object'
      && (entry as Record<string, unknown>).action === 'quickCreate') && doc.quickCreate == null) {
    issues.push('a quickCreate command needs the document\'s own quickCreate block')
  }
  return issues
}

/**
 * The lowest grammar version a document's blocks require. An authoring
 * surface sets `version` from this after every edit, so editing one block can
 * never silently invalidate another the author did not touch.
 */
export function tablePresentationVersionFloor(document: Partial<TablePresentationDocument>): 1 | 2 | 3 | 4 | 5 {
  if (document.commands != null) return 5
  if (document.picker != null) return 4
  if (document.workspace != null) return 3
  if (document.map != null || document.related != null || document.peek != null || document.timeline != null) return 2
  return 1
}

/* ── which commands a host DRAWS (#2375) ─────────────────────────────────────
 *
 * The document says what the deployment WANTS beside the mark; the host says
 * what it is AUTHORISED and ABLE to run here. The answer is the intersection,
 * in declared order — and every declared command that fell out is NAMED, never
 * silently lost, because an administrator who configured a button that does
 * not appear needs to be told which one and why.
 *
 * Pure: it reads nothing, grants nothing and fetches nothing. `governed` is
 * the host's own authorised set — whether quick create has a governed form and
 * seam here, whether linking is offered here, and the collection-level verbs
 * `core.my_entity_actions` answered for this caller.
 */
export interface GovernedTableCommands {
  /** The host has a governed quick-create seam and form for this entity. */
  quickCreate: boolean
  /** The host offers linking an existing record here. */
  linkExisting: boolean
  /** Collection-level (`cardinality = 'none'`) tool names this caller may be offered. */
  actions: ReadonlySet<string>
}

export interface ResolvedTableCommands {
  /** What to draw, in declared order. A host puts at most three beside the mark. */
  commands: TableCommand[]
  /** `declared` — the document's list answered. `derived` — it declares none. */
  source: 'declared' | 'derived'
  /** Every declared command that is not drawn, by name, and why. */
  issues: string[]
}

/** The derived fallback's two entries — what every grid drew before commands were data. */
export const DERIVED_QUICK_CREATE_COMMAND: TableCommand =
  { id: 'new', label: 'New', kind: 'primary', action: 'quickCreate' }
export const DERIVED_LINK_EXISTING_COMMAND: TableCommand =
  { id: 'link', label: 'Link existing', kind: 'secondary', action: 'linkExisting' }

const commandResolves = (action: string, governed: GovernedTableCommands): boolean =>
  action === 'quickCreate' ? governed.quickCreate
    : action === 'linkExisting' ? governed.linkExisting
      : governed.actions.has(action)

/**
 * Resolve the commands a host draws.
 *
 * `derive` (default true) says whether an ABSENT list falls back to the
 * derived commands; a host that has no command surface of its own passes
 * false and draws nothing. An explicit empty list always draws nothing — it is
 * an authored "no commands", not an absence.
 *
 * A malformed list is reported and draws NOTHING rather than being guessed at,
 * and rather than falling back to the derived list: a document that says
 * something unreadable has still said something, and silently substituting a
 * different answer is the "looks right, behaves wrong" shape.
 */
export function resolveTableCommands({ document, governed, derive = true }: {
  document: Partial<TablePresentationDocument> | null | undefined
  governed: GovernedTableCommands
  derive?: boolean
}): ResolvedTableCommands {
  const declared = document?.commands
  if (declared == null) {
    if (!derive) return { commands: [], source: 'derived', issues: [] }
    const commands: TableCommand[] = []
    if (governed.quickCreate) commands.push({ ...DERIVED_QUICK_CREATE_COMMAND })
    if (governed.linkExisting) commands.push({ ...DERIVED_LINK_EXISTING_COMMAND })
    return { commands, source: 'derived', issues: [] }
  }
  const problems = validateTableCommands(declared, document)
  if (problems.length > 0) return { commands: [], source: 'declared', issues: problems }
  const commands: TableCommand[] = []
  const issues: string[] = []
  for (const command of declared) {
    if (commandResolves(command.action, governed)) commands.push({ ...command })
    else issues.push(`command ${command.id} (${command.action}) is not available here, so it is not shown`)
  }
  return { commands, source: 'declared', issues }
}

/**
 * Resolve a requested lens against both experience data and this host's actual
 * renderers. Any malformed/unsupported declaration falls back to the neutral
 * grid document with an explicit issue; it cannot select a blank render branch.
 */
export function resolveTablePresentation(
  value: unknown,
  requested?: string | null,
  supported: readonly TablePresentation[] = TABLE_PRESENTATIONS,
): ResolvedTablePresentation {
  const issues = validateTablePresentation(value)
  if (issues.length > 0) {
    return { document: DEFAULT_TABLE_PRESENTATION, active: 'grid', status: 'fallback', issues }
  }
  const document = canonicalTablePresentation(value) as TablePresentationDocument
  const deprecations = tablePresentationDeprecations(value)
  const supportedSet = new Set(supported)
  const desired = requested ?? document.defaultPresentation
  const active = document.availablePresentations.find((kind) => kind === desired && supportedSet.has(kind))
    ?? document.availablePresentations.find((kind) => supportedSet.has(kind))
    ?? 'grid'
  const resolutionIssues: string[] = []
  if (!document.availablePresentations.includes(desired as TablePresentation)) {
    resolutionIssues.push(`requested presentation ${desired} is not available`)
  } else if (!supportedSet.has(desired as TablePresentation)) {
    resolutionIssues.push(`presentation ${desired} has no renderer in this host`)
  }
  if (!supportedSet.has(active)) {
    resolutionIssues.push('no configured presentation has a renderer; using grid fallback')
  }
  return {
    document,
    active: supportedSet.has(active) ? active : 'grid',
    status: resolutionIssues.length > 0 ? 'fallback' : 'resolved',
    issues: resolutionIssues,
    ...(deprecations.length > 0 ? { deprecations } : {}),
  }
}

/* ── the server envelope, and this host's renderers (#1982 closure) ─────────
 *
 * `resolveTablePresentation` above is #1987's contract and stays exactly as it
 * is: it takes a raw DOCUMENT and decides everything, which is what a host had
 * to do when the document was all the server sent.
 *
 * Since core 2026090915 the server sends its own resolution —
 * `describe_experience(...).presentation_resolved` — and that answer is
 * authoritative for the two questions a browser cannot answer correctly:
 *
 *   · WHICH LAYER decided (user > tenant > solution > persona > system). The
 *     client never sees the losing rows, so it cannot say where a value came
 *     from, and an admin looking at a Table Manager needs exactly that.
 *   · whether a REQUESTED lens is admissible. A stale local preference must be
 *     checked against what is governed, not against what the browser last saw.
 *
 * What stays client-side is the one fact the database has no business knowing:
 * which lenses THIS BUILD can actually draw. A host's renderer inventory is a
 * property of the bundle, not of the deployment, and a portal that ships two
 * renderers must not be told it has four because the console does.
 *
 * So this function is the join: the server says what is governed, the host says
 * what it can draw, and the result names every lens that fell out and why.
 */

/** Which layer answered, and how the active lens was chosen. */
export interface TablePresentationProvenance {
  /** `declared` — a stored document answered. `fallback` — none did. */
  source: 'declared' | 'fallback'
  /** Present on `fallback`: why there was nothing to resolve. */
  reason?: string
  scope?: string
  persona?: string | null
  host?: string | null
  version?: number
  experienceId?: string
  /** How `active` was chosen. `refused` means a request was not on offer. */
  activeSource?: 'default' | 'requested' | 'refused'
}

/** `core.resolve_table_presentation`'s answer, as it arrives on the wire. */
export interface TablePresentationEnvelope {
  entity?: string | null
  document?: unknown
  available?: unknown
  default?: unknown
  active?: unknown
  provenance?: unknown
  issues?: unknown
  /** The version-2 members (#2276). Absent on a version-1 declaration. */
  map?: unknown
  related?: unknown
  /** The version-4 member (#2351). Null where the entity declares no rail. */
  picker?: unknown
}

export interface HostResolvedTablePresentation extends ResolvedTablePresentation {
  /** What the deployment OFFERS, in declared order — server truth. */
  available: TablePresentation[]
  /** The governed default, which is not always what is active. */
  defaultPresentation: TablePresentation
  /** Which layer decided, and how `active` was chosen. */
  provenance: TablePresentationProvenance
  /** Offered lenses this host has no renderer for — named, never silent. */
  unsupported: TablePresentation[]
  /**
   * Where this entity's coordinates live, when it declares any (#2276).
   *
   * Carried on THIS envelope rather than fetched separately because the server
   * already answered it in the same call — and because a second door would let
   * a surface draw a pin from one resolution while its lens came from another.
   */
  map: MapRecipe | null
  /** What a relationship workspace over this entity offers. */
  related: RelatedOffer | null
  /**
   * How this record's relationship-authoring rail groups and describes its
   * slots (#2351), or null where nothing is declared.
   *
   * Read off its own member rather than out of `document`, like `map` and
   * `related` beside it: the document is forwarded whole, but one block read
   * differently from its siblings is the one the next resolver change forgets.
   * Null is a first-class answer — the rail then renders every derived slot in
   * the order the metamodel produced, ungrouped, which is the truthful floor.
   */
  picker: PickerPresentation | null
}

const asPresentations = (value: unknown): TablePresentation[] =>
  keys(value).filter((k): k is TablePresentation => PRESENTATIONS.has(k))

/** A declared rail, or null — and a named issue for anything in between. */
const pickerOrNull = (value: unknown, issues: string[]): PickerPresentation | null => {
  if (value == null) return null
  const problems = validatePickerPresentation(value)
  if (problems.length > 0) {
    issues.push(...problems)
    return null
  }
  return value as PickerPresentation
}

const asProvenance = (value: unknown): TablePresentationProvenance => {
  const row = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  const source = row.source === 'declared' ? 'declared' : 'fallback'
  const activeSource = row.active_source
  return {
    source,
    ...(named(row.reason) ? { reason: row.reason } : {}),
    ...(named(row.scope) ? { scope: row.scope } : {}),
    ...(row.persona === null || named(row.persona) ? { persona: row.persona as string | null } : {}),
    ...(row.host === null || named(row.host) ? { host: row.host as string | null } : {}),
    ...(typeof row.version === 'number' ? { version: row.version } : {}),
    ...(named(row.experience_id) ? { experienceId: row.experience_id } : {}),
    ...(activeSource === 'default' || activeSource === 'requested' || activeSource === 'refused'
      ? { activeSource } : {}),
  }
}

/**
 * Narrow the server's governed answer to what this host can draw.
 *
 * Never throws and never returns a blank lens — the same contract the document
 * resolver holds, for the same reason: a list whose lens resolution fails is a
 * blank page, and every fallback here carries the lens NAME so a host can say
 * "Boards aren't available here" rather than silently showing something else.
 *
 * An envelope that is absent or unreadable is treated as "the server said
 * nothing", which resolves to the grid. That is not a defensive crouch: it is
 * the state during the first paint of every list, before the query resolves.
 */
export function resolveTablePresentationEnvelope(
  envelope: unknown,
  supported: readonly TablePresentation[] = TABLE_PRESENTATIONS,
): HostResolvedTablePresentation {
  const row = (envelope && typeof envelope === 'object' ? envelope : {}) as TablePresentationEnvelope
  const supportedSet = new Set(supported)
  const provenance = asProvenance(row.provenance)
  const serverIssues = keys(row.issues)

  const available = asPresentations(row.available)
  const offered = available.length > 0 ? available : (['grid'] as TablePresentation[])

  const asOffered = (value: unknown): TablePresentation | null =>
    typeof value === 'string' && (offered as string[]).includes(value) ? value as TablePresentation : null
  const governedDefault = asOffered(row.default) ?? offered[0]!
  const governedActive = asOffered(row.active) ?? governedDefault

  // The host's half. An offered lens with no renderer here is NAMED, and the
  // next offered lens that does have one takes over — grid last, because it is
  // the one every host is required to draw.
  const unsupported = offered.filter((kind) => !supportedSet.has(kind))
  const issues = [...serverIssues]
  let active: TablePresentation = governedActive
  if (!supportedSet.has(active)) {
    issues.push(`the ${active} lens has no renderer in this host`)
    active = offered.find((kind) => supportedSet.has(kind)) ?? 'grid'
  }
  if (!supportedSet.has(active)) {
    issues.push('no offered lens has a renderer in this host; showing the grid')
    active = 'grid'
  }

  // The DOCUMENT still matters — the card recipe, the quick-create subset and
  // the command-bar default all live in it, and #1987's renderers read them
  // from here. An unreadable one degrades to the neutral document rather than
  // taking the whole surface down with it.
  const documentIssues = row.document === null || row.document === undefined
    ? ['no table presentation is declared for this entity']
    : validateTablePresentation(row.document)
  const document = documentIssues.length > 0
    ? DEFAULT_TABLE_PRESENTATION
    : canonicalTablePresentation(row.document) as TablePresentationDocument
  const deprecations = documentIssues.length > 0 ? [] : tablePresentationDeprecations(row.document)
  // The document's own problems are ISSUES, not a silent substitution. Dropping
  // them here made an unreadable card recipe report `resolved` while rendering
  // the neutral document — the exact "looks right, behaves wrong" shape, and
  // the first run of the envelope suite caught it.
  issues.push(...documentIssues)

  return {
    document,
    available: offered,
    defaultPresentation: governedDefault,
    active,
    provenance,
    unsupported,
    map: asMapRecipe(row.map),
    related: row.related && typeof row.related === 'object' && !Array.isArray(row.related)
      ? row.related as RelatedOffer
      : null,
    /* VALIDATED, not merely shaped. The server is the authority on what was
       stored, but this envelope is also what a fixture and an older deployment
       answer, and a rail drawn from a malformed block would group slots under
       headings the trigger would have refused. A broken one is dropped WITH its
       issues named, which lands the surface on the ungrouped floor rather than
       on a heading nobody declared. */
    picker: pickerOrNull(row.picker, issues),
    status: issues.length > 0 || provenance.source === 'fallback' ? 'fallback' : 'resolved',
    issues,
    ...(deprecations.length > 0 ? { deprecations } : {}),
  }
}

/* ── the card recipe becomes column roles (#1982 closure) ───────────────────
 *
 * #1982 measured the defect precisely: *"DataGrid already supports per-column
 * compact roles, but only one console ListConfig opts into `compact: 'cards'`,
 * and those roles are host code rather than resolved card data."*
 *
 * So the `cards` lens is not a new renderer — building one would be the second
 * grid #959 spent a release removing, and #964's rule is that where a surface
 * legitimately differs it is SUBTRACTION from the shared component. It is
 * `<DataGrid compact="cards">`, which already exists, with its column roles
 * DERIVED from the governed card spec instead of typed into a page (rule 42).
 *
 * The mapping is the whole of it, and it is deliberately total: every column
 * the view carries gets a role, because a column with no role falls into
 * `detail` by default and the card then silently shows a field the recipe did
 * not ask for.
 */
export type CardColumnRole = 'primary' | 'detail' | 'actions' | 'hidden'

/**
 * Assign each of a view's columns its role on the card, from the governed
 * recipe. Columns the recipe does not mention are HIDDEN, not detail: a card is
 * a chosen summary, and defaulting the unmentioned to visible turns it back
 * into the grid it exists to replace.
 *
 * `badge`, `accent` and `icon` are consumed by the renderer as decoration
 * rather than drawn as cells, so they are hidden here too — surfacing them
 * twice is the duplication the recipe exists to prevent.
 */
export function cardColumnRoles(
  card: TableCardSpec | undefined,
  columns: readonly string[],
): Record<string, CardColumnRole> {
  const roles: Record<string, CardColumnRole> = {}
  if (!card) {
    // No recipe: every column stays a detail. This is the honest answer for a
    // declaration that offers `cards` without one — which #2116's trigger
    // refuses at the door, so it can only arrive on a pre-trigger row.
    for (const column of columns) roles[column] = 'detail'
    return roles
  }
  const details = new Set(card.details ?? [])
  const decoration = new Set([card.badge, card.accent, card.icon].filter(named))
  for (const column of columns) {
    roles[column] = column === card.primary ? 'primary'
      : details.has(column) ? 'detail'
      : decoration.has(column) ? 'hidden'
      : 'hidden'
  }
  return roles
}

/* ── the RELATED surface's own finite vocabulary (#2276) ────────────────────
 *
 * A relationship workspace and a list are two different offers, and conflating
 * them is how a lens nothing can draw reaches a surface. `board` and `calendar`
 * need a group-by and a date column and have no meaning over a record's related
 * groups; `map` and `peek` have no meaning over a standalone list. The two sets
 * overlap in `grid` and `cards` and are otherwise disjoint.
 *
 * `metro` LEFT this set in #2324, and where it went is the whole ruling: it does
 * not describe how one relationship list is rendered, it describes how the
 * record's complete relationship network is laid out. It is a graph LAYOUT now.
 * The vocabulary VALUE is not deleted — core `2026091615` moves the
 * `core.view_kind` row from the `related` surface to a new `graph` surface,
 * keeping its label and its description, because a shared value is not thrown
 * away merely because one surface stops offering it.
 *
 * The authority is `core.view_kind.surfaces`, which is DATA — this constant is
 * the host-side mirror of those rows, and it exists for the same reason
 * `TABLE_PRESENTATIONS` does: a browser has to know which lenses THIS BUILD can
 * draw, and that is a property of the bundle rather than of the deployment.
 */
export const RELATED_PRESENTATIONS = ['grid', 'cards', 'map', 'peek', 'timeline'] as const
export type RelatedPresentation = typeof RELATED_PRESENTATIONS[number]

/* ── the WHOLE-RECORD workspace, and its two vocabularies (#2324) ───────────
 *
 * A record's Relationships workspace has two primary MODES, and inside Graph a
 * secondary switcher places the same nodes and edges two ways.
 *
 * The split between these two constants is deliberate, and it is rule 42's
 * inverse. "List and Graph" is a JUDGEMENT — the ruling says the workspace has
 * exactly two primary modes — so it is written down once, here and in the
 * migration, with the reason beside it. "Network and Metro" is a VOCABULARY:
 * each is a way of placing a graph, each needs a renderer, and a third one is an
 * INSERT plus a renderer. So what a DEPLOYMENT offers is read off the server's
 * answer, and this constant is only what THIS BUILD can draw.
 */
export const WORKSPACE_MODES = ['list', 'graph'] as const
export type WorkspaceMode = typeof WORKSPACE_MODES[number]

/* LEVELS is the third member and the first in order (#2406). It is the leading
 * design's Connections Graph: the record on the left, one column per hop and one
 * band per governed relationship group. It is first because it is the layout a
 * graph OPENS in on this build — the floor below and `DEFAULT_WORKSPACE` name it
 * — and the order of this constant is the order a switcher offers.
 *
 * Being in this constant is only half of being offered. A deployment offers
 * what `core.view_kind` registers for the `graph` surface, so on a server that
 * has not registered `levels` the resolver drops it as not offered and this
 * build draws Network exactly as it did before. */
export const GRAPH_LAYOUTS = ['levels', 'network', 'metro'] as const
export type GraphLayout = typeof GRAPH_LAYOUTS[number]

/** How a graph is laid out, bounded and grouped. Never where a node SITS. */
export interface GraphContract {
  layouts: GraphLayout[]
  defaultLayout: GraphLayout
  /** Hops drawn on open. One, unless a deployment says otherwise. */
  initialDepth?: number
  /** Hops a person may expand to. Narrows `core.graph_max_depth`, never widens it. */
  maximumDepth?: number
  /** The finite grouping vocabulary. One value today; a second is a renderer. */
  groupBy?: 'relationshipType'
}

/**
 * What a record's whole Relationships workspace offers.
 *
 * A SIBLING of `related`, never a member of it. The ruling is explicit that the
 * per-slot lens and this have "different scope and semantics", and nesting this
 * inside `related` would have made confusing them the natural reading.
 *
 * What is deliberately absent: coordinates (§8 — hand-arranged positions are not
 * platform data), any relationship or entity key (the contract says how a
 * workspace READS, not what is in it), and any component, callback or colour.
 */
export interface WorkspaceContract {
  modes: WorkspaceMode[]
  defaultMode: WorkspaceMode
  graph?: GraphContract
}

const MODES = new Set<string>(WORKSPACE_MODES)
const LAYOUTS = new Set<string>(GRAPH_LAYOUTS)

/**
 * Validate a whole-record workspace contract.
 *
 * Mirrors `core.validate_workspace_contract` in what it refuses, with ONE
 * deliberate omission: the depth CEILING. `core.graph_max_depth` is a
 * deployment setting, the browser holds no settings table, and a client that
 * guessed at 4 would disagree with the trigger that knows — the same split
 * `validateMapRecipe` keeps for "is this column numeric".
 */
export function validateWorkspaceContract(value: unknown, path = 'workspace'): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [`${path} must be an object`]
  const row = value as Record<string, unknown>
  const issues: string[] = []
  rejectUnknown(row, ['modes', 'defaultMode', 'graph'], path, issues)

  const modes = keys(row.modes)
  if (!Array.isArray(row.modes) || modes.length === 0) {
    issues.push(`${path} must offer at least one mode`)
  }
  for (const mode of modes) if (!MODES.has(mode)) issues.push(`${path} offers unknown mode ${mode}`)
  for (const mode of new Set(duplicates(modes))) issues.push(`${path} offers duplicate mode ${mode}`)
  // LIST IS NOT OPTIONAL. §7 of the ruling: List is the complete accessible
  // fallback and the only mode that can add a relationship. A workspace with
  // only Graph has neither, so it is refused rather than degraded.
  if (Array.isArray(row.modes) && !modes.includes('list')) {
    issues.push(`${path} always offers the list mode — it is the accessible fallback`)
  }
  if (!named(row.defaultMode) || !MODES.has(row.defaultMode)) {
    issues.push(`${path} has an unsupported default mode ${String(row.defaultMode ?? '')}`)
  } else if (!modes.includes(row.defaultMode)) {
    issues.push(`${path} default mode ${row.defaultMode} is not among the modes it offers`)
  }

  if (row.graph == null) return issues
  if (typeof row.graph !== 'object' || Array.isArray(row.graph)) {
    issues.push(`${path}.graph must be an object`)
    return issues
  }
  const graph = row.graph as Record<string, unknown>
  rejectUnknown(graph, ['layouts', 'defaultLayout', 'initialDepth', 'maximumDepth', 'groupBy'],
    `${path}.graph`, issues)

  const layouts = keys(graph.layouts)
  if (!Array.isArray(graph.layouts) || layouts.length === 0) {
    issues.push(`${path}.graph must offer at least one layout`)
  }
  for (const l of layouts) if (!LAYOUTS.has(l)) issues.push(`${path}.graph offers unknown layout ${l}`)
  for (const l of new Set(duplicates(layouts))) issues.push(`${path}.graph offers duplicate layout ${l}`)
  if (!named(graph.defaultLayout) || !LAYOUTS.has(graph.defaultLayout)) {
    issues.push(`${path}.graph has an unsupported default layout ${String(graph.defaultLayout ?? '')}`)
  } else if (!layouts.includes(graph.defaultLayout)) {
    issues.push(`${path}.graph default layout ${graph.defaultLayout} is not among the layouts it offers`)
  }

  for (const depth of ['initialDepth', 'maximumDepth'] as const) {
    if (graph[depth] == null) continue
    const n = graph[depth]
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 1) {
      issues.push(`${path}.graph ${depth} is a whole number of hops`)
    }
  }
  const initial = graph.initialDepth
  const maximum = graph.maximumDepth
  if (typeof initial === 'number' && typeof maximum === 'number' && initial > maximum) {
    issues.push(`${path}.graph opens at ${initial} hops and expands to at most ${maximum}`)
  }
  if (graph.groupBy != null && graph.groupBy !== 'relationshipType') {
    issues.push(`${path}.graph groups by relationshipType, not by ${String(graph.groupBy)}`)
  }
  return issues
}

/**
 * Where a record's coordinates live, and what a pin is called.
 *
 * Field KEYS only. No SQL, no callback, no component name, no provider, no key,
 * no geocoder — the whole point is that a recipe is a sentence about COLUMNS
 * that a migration can ship and an administrator can read.
 *
 * `title`/`subtitle` are optional and usually absent: `core.register_entity_title`
 * already declares what a record is called, and naming it again here would let a
 * pin disagree with every other surface about the same record (rule 42). They
 * exist for an entity whose canonical title is not what a pin should say.
 */
/**
 * Which sections of the TARGET's canonical form a preview shows (#2281).
 *
 * Section IDENTITIES, not titles and not fields: core 2026091520 made them
 * declared and stored precisely so a recipe can name one and the trust boundary
 * can check it. An absent recipe is not an empty preview — #2281 falls back to
 * the first visible, non-empty section in the resolved form order, which is a
 * decision for the renderer because only it has the resolved form.
 */
export interface PeekRecipe {
  sections: string[]
}

export interface MapRecipe {
  lat: string
  lng: string
  title?: string
  subtitle?: string
}

/* ── the TIMELINE recipe (#2323) ────────────────────────────────────────────
 *
 * A finite sentence about ONE date column and a small set of series over it.
 * No SQL, no format string, no colour, no label and no expression — every one
 * of those is a way for a personal preference to become code, which is the door
 * #2323 closes in its own words: *"A personal preference must not inject
 * arbitrary HTML, SQL, format strings, colors, or labels."*
 *
 * Labels come from `core.field_meta` plus the aggregation, so they are DERIVED
 * (rule 42). A `label` key here would be a second source of truth for what a
 * column is called, and the axis would be free to disagree with every grid in
 * the deployment about the same field.
 *
 * The recipe is TOTAL — all six members required. A partial governed recipe
 * would mean a reader cannot tell what a chart does without also knowing a
 * default table; the personal half is partial and lives in `TimelineOverride`,
 * which is a different object precisely so the two cannot be confused.
 */
export const TIMELINE_GRANULARITIES = ['hour', 'day', 'week', 'month', 'quarter', 'year'] as const
export type TimelineGranularity = typeof TIMELINE_GRANULARITIES[number]

export const TIMELINE_AGGREGATIONS = ['min', 'max', 'avg', 'median', 'sum', 'count'] as const
export type TimelineAggregation = typeof TIMELINE_AGGREGATIONS[number]

export const TIMELINE_TIME_ZONES = ['viewer', 'utc'] as const
export type TimelineTimeZone = typeof TIMELINE_TIME_ZONES[number]

export const TIMELINE_EMPTY_BUCKETS = ['gap', 'zero'] as const
export type TimelineEmptyBuckets = typeof TIMELINE_EMPTY_BUCKETS[number]

/**
 * Governed maxima. These are a COST GUARD, not a preference, so they are
 * constants rather than settings — rule 44's stated exemption, taken
 * deliberately: a deployment does not get to edit its own query budget, any
 * more than it edits its licence degrade ladder.
 *
 * `TIMELINE_MAX_BUCKETS` is 744 because that is an hour-granular 31-day month —
 * the largest window anyone asked for at the finest granularity, and the number
 * a reader can check rather than take on faith. The server enforces the same
 * three; this constant exists so a menu can refuse before a round trip, never
 * so a client can decide.
 */
export const TIMELINE_MAX_SERIES = 4
export const TIMELINE_MAX_BUCKETS = 744

/** `count` takes no field; every other aggregation requires one. */
export interface TimelineSeriesSpec {
  /** A governed column key on the TARGET entity. Absent only for `count`. */
  field?: string
  aggregation: TimelineAggregation
}

export interface TimelineRange {
  value: number
  unit: TimelineGranularity
}

export interface TimelineRecipe {
  timeField: string
  granularity: TimelineGranularity
  timeZone: TimelineTimeZone
  range: TimelineRange
  series: TimelineSeriesSpec[]
  emptyBuckets: TimelineEmptyBuckets
}

/**
 * A person's reading of one slot's timeline: any subset of the governed recipe.
 *
 * Separate from `TimelineRecipe` and deliberately not a `Partial<>` alias — the
 * two are stored in different places, validated at different boundaries and
 * repaired differently, and collapsing them into one type is how a personal
 * value acquires the authority of a governed one. `timeField` is overridable
 * because §5's menu offers it; what is NOT overridable is anything that is not
 * on this list, which is every key that could carry a format, a colour or a
 * label.
 */
export type TimelineOverride = Partial<TimelineRecipe>

const GRANULARITIES = new Set<string>(TIMELINE_GRANULARITIES)
const AGGREGATIONS = new Set<string>(TIMELINE_AGGREGATIONS)
const TIME_ZONES = new Set<string>(TIMELINE_TIME_ZONES)
const EMPTY_BUCKETS = new Set<string>(TIMELINE_EMPTY_BUCKETS)

/** How many buckets a range at a granularity produces — the client's own floor. */
const HOURS: Record<TimelineGranularity, number> = {
  hour: 1, day: 24, week: 168, month: 744, quarter: 2232, year: 8784,
}
export function timelineBucketCount(range: TimelineRange, granularity: TimelineGranularity): number {
  // Upper bound, not an exact count: months and years are uneven, and the
  // honest thing for a budget check is the largest number the window could
  // produce. An exact count belongs to the server, which knows the calendar and
  // the zone; a client that computed one would disagree with it across a DST
  // boundary and refuse a request the server would have honoured.
  return Math.ceil((range.value * HOURS[range.unit]) / HOURS[granularity])
}

const validateSeries = (value: unknown, path: string, issues: string[]): void => {
  if (!Array.isArray(value) || value.length === 0) {
    issues.push(`${path} must contain at least one series`)
    return
  }
  if (value.length > TIMELINE_MAX_SERIES) {
    issues.push(`${path} may contain at most ${TIMELINE_MAX_SERIES} series`)
  }
  const seen: string[] = []
  value.forEach((entry, index) => {
    const where = `${path}[${index}]`
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      issues.push(`${where} must be an object`)
      return
    }
    const row = entry as Record<string, unknown>
    rejectUnknown(row, ['field', 'aggregation'], where, issues)
    const aggregation = row.aggregation
    if (!named(aggregation) || !AGGREGATIONS.has(aggregation)) {
      issues.push(`${where} has an unsupported aggregation ${String(aggregation ?? '')}`)
      return
    }
    if (row.field != null && !named(row.field)) {
      issues.push(`${where} field must name a column`)
      return
    }
    // `count` counts rows, so a field on it is not a harmless extra: it reads
    // as "count this column's non-nulls", which is a DIFFERENT number. Refused
    // rather than ignored.
    if (aggregation === 'count' && row.field != null) {
      issues.push(`${where} counts visible rows, so it takes no field`)
      return
    }
    if (aggregation !== 'count' && !named(row.field)) {
      issues.push(`${where} aggregation ${aggregation} needs a numeric field`)
      return
    }
    const identity = `${aggregation}:${named(row.field) ? row.field : ''}`
    if (seen.includes(identity)) issues.push(`${path} repeats the series ${identity}`)
    seen.push(identity)
  })
}

const validateRange = (value: unknown, path: string, issues: string[]): void => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    issues.push(`${path} must be an object`)
    return
  }
  const row = value as Record<string, unknown>
  rejectUnknown(row, ['value', 'unit'], path, issues)
  if (typeof row.value !== 'number' || !Number.isInteger(row.value) || row.value < 1) {
    issues.push(`${path}.value must be a positive whole number`)
  }
  if (!named(row.unit) || !GRANULARITIES.has(row.unit)) {
    issues.push(`${path}.unit is unsupported: ${String(row.unit ?? '')}`)
  }
}

/**
 * The SHAPE of a timeline recipe, and nothing about the catalogue.
 *
 * Deliberately NOT checked here, for the reason `validateMapRecipe` gives about
 * numeric coordinates: the browser holds no catalogue, so it cannot answer
 * "is `observed_at` a date in storage" or "does the target describe this
 * column". A client that guessed would disagree with the trigger that knows.
 * Those are `core.validate_timeline_recipe`'s, at the boundary.
 */
export function validateTimelineRecipe(value: unknown, path = 'timeline recipe'): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [`${path} must be an object`]
  const row = value as Record<string, unknown>
  const issues: string[] = []
  rejectUnknown(row, ['timeField', 'granularity', 'timeZone', 'range', 'series', 'emptyBuckets'], path, issues)

  if (!named(row.timeField)) issues.push(`${path} must name a time field`)
  if (!named(row.granularity) || !GRANULARITIES.has(row.granularity)) {
    issues.push(`${path} has an unsupported granularity ${String(row.granularity ?? '')}`)
  }
  if (!named(row.timeZone) || !TIME_ZONES.has(row.timeZone)) {
    issues.push(`${path} has an unsupported time zone ${String(row.timeZone ?? '')}`)
  }
  if (!named(row.emptyBuckets) || !EMPTY_BUCKETS.has(row.emptyBuckets)) {
    issues.push(`${path} has an unsupported empty-bucket rule ${String(row.emptyBuckets ?? '')}`)
  }
  validateRange(row.range, `${path}.range`, issues)
  validateSeries(row.series, `${path}.series`, issues)

  // The budget, checked only once both halves are readable — a bucket count
  // over a malformed range is a number about nothing.
  if (named(row.granularity) && GRANULARITIES.has(row.granularity)
      && row.range && typeof row.range === 'object' && !Array.isArray(row.range)) {
    const range = row.range as Record<string, unknown>
    if (typeof range.value === 'number' && named(range.unit) && GRANULARITIES.has(range.unit)) {
      const buckets = timelineBucketCount(
        { value: range.value, unit: range.unit as TimelineGranularity },
        row.granularity as TimelineGranularity)
      if (buckets > TIMELINE_MAX_BUCKETS) {
        issues.push(`${path} asks for about ${buckets} buckets; at most ${TIMELINE_MAX_BUCKETS} are served`)
      }
    }
  }
  return issues
}

/** A person's partial reading. Every member it DOES carry is held to the same rules. */
export function validateTimelineOverride(value: unknown, path = 'timeline preference'): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [`${path} must be an object`]
  const row = value as Record<string, unknown>
  const issues: string[] = []
  rejectUnknown(row, ['timeField', 'granularity', 'timeZone', 'range', 'series', 'emptyBuckets'], path, issues)

  if (row.timeField != null && !named(row.timeField)) issues.push(`${path} timeField must name a field`)
  if (row.granularity != null && (!named(row.granularity) || !GRANULARITIES.has(row.granularity))) {
    issues.push(`${path} has an unsupported granularity ${String(row.granularity)}`)
  }
  if (row.timeZone != null && (!named(row.timeZone) || !TIME_ZONES.has(row.timeZone))) {
    issues.push(`${path} has an unsupported time zone ${String(row.timeZone)}`)
  }
  if (row.emptyBuckets != null && (!named(row.emptyBuckets) || !EMPTY_BUCKETS.has(row.emptyBuckets))) {
    issues.push(`${path} has an unsupported empty-bucket rule ${String(row.emptyBuckets)}`)
  }
  if (row.range != null) validateRange(row.range, `${path}.range`, issues)
  if (row.series != null) validateSeries(row.series, `${path}.series`, issues)
  return issues
}

/**
 * The EFFECTIVE recipe: the governed one, with the person's named members
 * applied over it — §3's *"the finite effective recipe"*.
 *
 * An UNREADABLE override is dropped whole rather than merged member by member.
 * A half-applied preference is a chart nobody authored: it is neither what the
 * administrator declared nor what the person asked for, and no surface could
 * explain it. `series` replaces rather than concatenating, for the same reason —
 * appending would let a stale preference silently raise the series count past
 * the governed maximum.
 */
export function effectiveTimelineRecipe(
  recipe: TimelineRecipe,
  override?: unknown,
): { recipe: TimelineRecipe; overridden: boolean; issues: string[] } {
  if (override == null) return { recipe, overridden: false, issues: [] }
  const issues = validateTimelineOverride(override)
  if (issues.length > 0) return { recipe, overridden: false, issues }
  const row = override as TimelineOverride
  const merged: TimelineRecipe = {
    timeField: row.timeField ?? recipe.timeField,
    granularity: row.granularity ?? recipe.granularity,
    timeZone: row.timeZone ?? recipe.timeZone,
    range: row.range ?? recipe.range,
    series: row.series ?? recipe.series,
    emptyBuckets: row.emptyBuckets ?? recipe.emptyBuckets,
  }
  // The MERGE can be invalid where neither half was — an hour granularity over
  // a governed year range is the obvious one. Checked after merging, because
  // that is the document the door would actually be asked to serve.
  const mergedIssues = validateTimelineRecipe(merged, 'effective timeline recipe')
  if (mergedIssues.length > 0) return { recipe, overridden: false, issues: mergedIssues }
  return { recipe: merged, overridden: true, issues: [] }
}

/** A recipe from the wire, or null — never a half-read one. */
export function asTimelineRecipe(value: unknown): TimelineRecipe | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return validateTimelineRecipe(value).length === 0 ? value as TimelineRecipe : null
}

/** What a relationship workspace offers, and which of those is the default. */
export interface RelatedOffer {
  available: RelatedPresentation[]
  default: RelatedPresentation
  /**
   * A source's explicit override for ONE far entity.
   *
   * Keyed by TARGET ENTITY rather than by slot, because map capability is
   * decided by what the far record carries and by nothing on the edge — which is
   * also what keeps every layer of this free of a relationship key.
   */
  perTarget?: Record<string, Partial<RelatedOffer>>
  /**
   * A source's explicit offer and default for ONE registered relationship SLOT,
   * keyed by slot identity (`<relType>:<direction>:<otherEntity>`).
   *
   * This is the unit an owner actually chooses by: two slots reaching the same
   * entity — billing addresses and sites — are not the same question, which is
   * precisely what `perTarget` above cannot express. `perTarget` remains
   * readable as an inherited default so existing documents keep resolving.
   *
   * `target` is DECLARED rather than parsed out of the key. The key contains the
   * entity, and reading it out would make a renamed relationship type a silent
   * capability change.
   */
  perSlot?: Record<string, Partial<RelatedOffer> & { target?: string }>
}

const RELATED = new Set<string>(RELATED_PRESENTATIONS)

export function validateMapRecipe(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return ['map recipe must be an object']
  const row = value as Record<string, unknown>
  const issues: string[] = []
  rejectUnknown(row, ['lat', 'lng', 'title', 'subtitle'], 'map recipe', issues)
  for (const axis of ['lat', 'lng'] as const) {
    if (!named(row[axis])) issues.push(`map recipe must name a ${axis} column`)
  }
  if (named(row.lat) && row.lat === row.lng) {
    issues.push('map recipe cannot read latitude and longitude from the same column')
  }
  for (const key of ['title', 'subtitle'] as const) {
    if (row[key] != null && !named(row[key])) issues.push(`map recipe ${key} must name a field`)
  }
  // Deliberately NOT checked here: whether the columns exist and are numeric.
  // The browser has no catalogue, `field_meta.kind` cannot answer "is this a
  // number in storage", and a client that guessed would disagree with the
  // trigger that knows. That check is core.validate_presentation_extensions'.
  return issues
}

export function validatePeekRecipe(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return ['peek recipe must be an object']
  const row = value as Record<string, unknown>
  const issues: string[] = []
  rejectUnknown(row, ['sections'], 'peek recipe', issues)

  const sections = keys(row.sections)
  if (!Array.isArray(row.sections) || sections.length === 0) {
    issues.push('peek recipe must name at least one section')
  }
  if (Array.isArray(row.sections) && row.sections.some((s) => !named(s))) {
    issues.push('peek recipe names sections by their declared id')
  }
  for (const id of new Set(duplicates(sections))) {
    issues.push(`peek recipe names section ${id} more than once`)
  }
  // Deliberately NOT checked here: whether the entity declares these sections.
  // The browser holds no form registry, and a client that guessed would
  // disagree with the trigger that knows — the same split `validateMapRecipe`
  // keeps for numeric coordinates.
  return issues
}

export function validateRelatedOffer(value: unknown, path = 'related'): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [`${path} must be an object`]
  const row = value as Record<string, unknown>
  const issues: string[] = []
  rejectUnknown(row, ['available', 'default', 'perTarget', 'perSlot'], path, issues)

  const available = keys(row.available)
  if (!Array.isArray(row.available) || available.length === 0) {
    issues.push(`${path} must offer at least one presentation`)
  }
  for (const key of available) if (!RELATED.has(key)) issues.push(`${path} offers unsupported presentation ${key}`)
  for (const key of new Set(duplicates(available))) issues.push(`${path} offers duplicate presentation ${key}`)

  if (!named(row.default) || !RELATED.has(row.default)) {
    issues.push(`${path} has an unsupported default ${String(row.default ?? '')}`)
  } else if (!available.includes(row.default)) {
    issues.push(`${path} default ${row.default} is not among the presentations it offers`)
  }

  if (row.perTarget != null) {
    if (typeof row.perTarget !== 'object' || Array.isArray(row.perTarget)) {
      issues.push(`${path}.perTarget must be an object keyed by target entity`)
    } else {
      for (const [entity, override] of Object.entries(row.perTarget as Record<string, unknown>)) {
        if (!override || typeof override !== 'object' || Array.isArray(override)) {
          issues.push(`${path}.perTarget.${entity} must be an object`)
          continue
        }
        const child = override as Record<string, unknown>
        rejectUnknown(child, ['available', 'default'], `${path}.perTarget.${entity}`, issues)
        const childAvailable = keys(child.available)
        for (const key of childAvailable) {
          if (!RELATED.has(key)) issues.push(`${path}.perTarget.${entity} offers unsupported presentation ${key}`)
        }
        if (child.default != null && named(child.default) && childAvailable.length > 0
            && !childAvailable.includes(child.default)) {
          issues.push(`${path}.perTarget.${entity} default ${child.default} is not among the presentations it offers`)
        }
      }
    }
  }
  if (row.perSlot != null) {
    if (typeof row.perSlot !== 'object' || Array.isArray(row.perSlot)) {
      issues.push(`${path}.perSlot must be an object keyed by relationship slot identity`)
    } else {
      for (const [slot, override] of Object.entries(row.perSlot as Record<string, unknown>)) {
        if (!override || typeof override !== 'object' || Array.isArray(override)) {
          issues.push(`${path}.perSlot.${slot} must be an object`)
          continue
        }
        if (slot.trim() === '') issues.push(`${path}.perSlot has an empty slot identity`)
        const child = override as Record<string, unknown>
        rejectUnknown(child, ['target', 'available', 'default'], `${path}.perSlot.${slot}`, issues)
        // The far entity is DECLARED. The slot identity contains it, and reading
        // it back out of the key is the inference that turns a rename into a
        // silent capability change.
        if (!named(child.target)) issues.push(`${path}.perSlot.${slot} must name the target entity it applies to`)
        const childAvailable = keys(child.available)
        for (const key of childAvailable) {
          if (!RELATED.has(key)) issues.push(`${path}.perSlot.${slot} offers unsupported presentation ${key}`)
        }
        if (child.default != null && named(child.default) && childAvailable.length > 0
            && !childAvailable.includes(child.default)) {
          issues.push(`${path}.perSlot.${slot} default ${child.default} is not among the presentations it offers`)
        }
      }
    }
  }
  // The "a map DEFAULT needs a recipe" rule is deliberately absent here too: it
  // is a question about the TARGET entity's document, which this function does
  // not hold. The trigger owns it, and the authoring surface reads the answer
  // off the resolved envelope rather than recomputing it.
  return issues
}

/* ── the PER-SLOT envelope, and this host's renderers (#2276) ───────────────
 *
 * `core.resolve_related_presentation` answers what is GOVERNED for EACH
 * registered relationship slot of one record: what that slot offers, the
 * administrator default, which rung decided, the target's map recipe, and
 * whether a personal choice was honoured or refused.
 *
 * Per SLOT, not per workspace, and the distinction is the owner's: "how do I
 * read the people here" and "how do I read the addresses here" are different
 * questions, and two slots reaching the same entity may still answer them
 * differently.
 *
 * What stays here is the one fact the database has no business knowing: which
 * lenses THIS BUILD can actually draw. That split is `resolveTablePresentationEnvelope`'s
 * and it is copied on purpose rather than generalised — the two surfaces have
 * different vocabularies and folding them into one function would mean a
 * `board` could reach a record's related groups.
 */

/** Which rung of the ladder answered. `platform` is the Grid floor. */
export type RelatedDecidingRung =
  | 'source-slot' | 'source-slot-legacy' | 'source' | 'target' | 'platform'

const RUNGS = new Set<string>(
  ['source-slot', 'source-slot-legacy', 'source', 'target', 'platform'])

export interface RelatedPresentationProvenance {
  decidingRung: RelatedDecidingRung
  /** How `active` was chosen. `refused` means a request was not on offer. */
  activeSource?: 'default' | 'requested' | 'refused'
  /** Set when a stored legacy value was migrated rather than obeyed. */
  migratedFrom?: string
  /** The TARGET entity's own resolution provenance, for the authoring surface. */
  target?: TablePresentationProvenance
}

/** The governed answer for ONE registered relationship slot. */
export interface HostResolvedSlotPresentation {
  /** The slot identity this answers for: `<relType>:<direction>:<otherEntity>`. */
  slot: string
  /** The far entity this slot draws. */
  target: string
  available: RelatedPresentation[]
  defaultPresentation: RelatedPresentation
  active: RelatedPresentation
  /** The target entity's declared coordinate recipe, or null. */
  map: MapRecipe | null
  /** The target entity's declared preview recipe, or null. */
  peek: PeekRecipe | null
  /** The target entity's declared timeline recipe, or null. */
  timeline: TimelineRecipe | null
  /** Does the TARGET resolve a canonical readable form? */
  peekCapable: boolean
  /**
   * Does the TARGET declare a recipe whose time field is STILL a described
   * date column? Two facts, not one — a recipe outlives the column it names,
   * and #2323 §2 asks the offer to withdraw when it stops being readable.
   */
  timelineCapable: boolean
  /** Does the TARGET declare a recipe? Capability is the far entity's property. */
  mapCapable: boolean
  provenance: RelatedPresentationProvenance
  /** Offered lenses this host has no renderer for — named, never silent. */
  unsupported: RelatedPresentation[]
  status: 'resolved' | 'fallback'
  issues: string[]
}

/** Every slot of one record, keyed by slot identity, in the order asked. */
export interface HostResolvedRelatedPage {
  entity: string
  slots: Record<string, HostResolvedSlotPresentation>
  /** Slot identities in the order the server answered them. */
  order: string[]
  /**
   * The WHOLE-RECORD reading (#2324): List or Graph, and Network or Metro
   * inside Graph.
   *
   * Beside the slots and never inside one. A workspace mode is a statement
   * about the record's entire relationship network; a slot lens is a statement
   * about one list. The ruling's own sentence — "they have different scope and
   * semantics" — is what this shape is.
   */
  workspace: HostResolvedWorkspace
  provenance: { source?: TablePresentationProvenance }
  status: 'resolved' | 'fallback'
}

const asRelated = (value: unknown): RelatedPresentation[] =>
  keys(value).filter((k): k is RelatedPresentation => RELATED.has(k))

/**
 * Narrow ONE slot's governed answer to what this host can draw.
 *
 * Never throws and never returns a blank lens, for the reason the table twin
 * gives: a section whose lens resolution fails is a blank section. A cell that
 * is absent or unreadable is treated as "the server said nothing", which
 * resolves to the Grid — and that is not a defensive crouch, it is the state
 * during the first paint of every record.
 */
export function resolveSlotPresentation(
  slot: string,
  cell: unknown,
  supported: readonly RelatedPresentation[] = RELATED_PRESENTATIONS,
): HostResolvedSlotPresentation {
  const row = (cell && typeof cell === 'object' ? cell : {}) as Record<string, unknown>
  const supportedSet = new Set(supported)
  const issues = [...keys(row.issues)]

  const offered = asRelated(row.available)
  const available = offered.length > 0 ? offered : (['grid'] as RelatedPresentation[])
  const asOffered = (value: unknown): RelatedPresentation | null =>
    typeof value === 'string' && (available as string[]).includes(value)
      ? value as RelatedPresentation : null
  const defaultPresentation = asOffered(row.default) ?? available[0]!
  let active = asOffered(row.active) ?? defaultPresentation

  const provRow = (row.provenance && typeof row.provenance === 'object'
    ? row.provenance : {}) as Record<string, unknown>
  const rung = provRow.deciding_rung
  const activeSource = provRow.active_source

  // An offered lens with no renderer HERE is named, and the next offered lens
  // that does have one takes over — Grid last, because it is the one every host
  // is required to draw.
  const unsupported = available.filter((kind) => !supportedSet.has(kind))
  if (!supportedSet.has(active)) {
    issues.push(`the ${active} lens has no renderer in this host`)
    active = available.find((kind) => supportedSet.has(kind)) ?? 'grid'
  }
  if (!supportedSet.has(active)) {
    issues.push('no offered lens has a renderer in this host; showing the grid')
    active = 'grid'
  }

  return {
    slot: named(row.slot) ? row.slot : slot,
    target: named(row.target) ? row.target : '',
    available,
    defaultPresentation,
    active,
    map: asMapRecipe(row.map),
    peek: asPeekRecipe(row.peek),
    timeline: asTimelineRecipe(row.timeline),
    mapCapable: row.mapCapable === true,
    peekCapable: row.peekCapable === true,
    timelineCapable: row.timelineCapable === true,
    provenance: {
      decidingRung: typeof rung === 'string' && RUNGS.has(rung)
        ? rung as RelatedDecidingRung : 'platform',
      ...(activeSource === 'default' || activeSource === 'requested' || activeSource === 'refused'
        ? { activeSource } : {}),
      ...(named(provRow.migrated_from) ? { migratedFrom: provRow.migrated_from } : {}),
      ...(provRow.target ? { target: asProvenance(provRow.target) } : {}),
    },
    unsupported,
    status: issues.length > 0 ? 'fallback' : 'resolved',
    issues,
  }
}

/* ── the WHOLE-RECORD answer, narrowed to what this build can draw (#2324) ──
 *
 * `core.resolve_related_presentation` answers the workspace beside the slots, in
 * the same envelope, because the source entity's document is where the contract
 * lives and that document is resolved exactly once for the page. A second door
 * would resolve the same ladder twice and the two answers could disagree about a
 * page that is on screen once.
 *
 * What stays here is the half the database has no business knowing: which
 * layouts THIS BUILD has a renderer for. That is a property of the bundle, and a
 * portal that ships no graph canvas must not be told it has one because the
 * console does.
 */

/** Which rung answered, and how the active mode and layout were chosen. */
export interface WorkspaceProvenance {
  /** `declared` — a stored contract answered. `platform` — none did. */
  source: 'declared' | 'platform'
  /** `refused` means a personal request was not on offer. */
  modeSource?: 'default' | 'requested' | 'refused'
  layoutSource?: 'default' | 'requested' | 'refused'
}

export interface HostResolvedGraph {
  /** What the deployment OFFERS and this host can draw. */
  layouts: GraphLayout[]
  defaultLayout: GraphLayout
  /** What is actually drawn. */
  layout: GraphLayout
  initialDepth: number
  maximumDepth: number
  groupBy: 'relationshipType'
  /** Offered layouts this host has no renderer for — named, never silent. */
  unsupported: GraphLayout[]
}

export interface HostResolvedWorkspace {
  modes: WorkspaceMode[]
  defaultMode: WorkspaceMode
  mode: WorkspaceMode
  graph: HostResolvedGraph
  provenance: WorkspaceProvenance
  status: 'resolved' | 'fallback'
  issues: string[]
}

/**
 * The floor, and it is the RULING rather than a guess.
 *
 * Reached whenever the server said nothing — which includes the first paint of
 * every record page, before the query resolves. A workspace that throws, or that
 * answers `undefined`, is a blank page; this is the state it is in instead.
 *
 * The LAYOUT half moved from Network to Levels with #2406's leading design. That
 * is the owner's choice of what a graph opens in, and core's own floor in
 * `core.resolve_workspace_contract` still names Network until it registers
 * `levels` and says so too — until then a real deployment answers Network and
 * this value is only ever the pre-answer paint, which is in List anyway.
 */
export const DEFAULT_WORKSPACE: HostResolvedWorkspace = {
  modes: ['list', 'graph'],
  defaultMode: 'list',
  mode: 'list',
  graph: {
    layouts: ['levels', 'network', 'metro'],
    defaultLayout: 'levels',
    layout: 'levels',
    initialDepth: 1,
    maximumDepth: 1,
    groupBy: 'relationshipType',
    unsupported: [],
  },
  provenance: { source: 'platform' },
  status: 'fallback',
  issues: [],
}

const asModes = (value: unknown): WorkspaceMode[] =>
  keys(value).filter((k): k is WorkspaceMode => MODES.has(k))
const asLayouts = (value: unknown): GraphLayout[] =>
  keys(value).filter((k): k is GraphLayout => LAYOUTS.has(k))

/** A whole number of hops from the wire, or null — never a half-read one. */
const asDepth = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1 ? value : null

/**
 * Narrow the governed whole-record answer to what this host can draw.
 *
 * Never throws and never returns a mode with nothing behind it, which is the
 * contract both sibling resolvers hold for the same reason: a workspace whose
 * resolution fails is a blank page. A host with NO graph renderer has Graph
 * withdrawn and is told so by name — the one refusal that cannot be wrong,
 * because only the bundle knows what it ships.
 */
export function resolveWorkspaceEnvelope(
  envelope: unknown,
  supported: readonly GraphLayout[] = GRAPH_LAYOUTS,
): HostResolvedWorkspace {
  if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope)) return DEFAULT_WORKSPACE
  const row = envelope as Record<string, unknown>
  const supportedSet = new Set(supported)
  const issues = [...keys(row.issues)]

  const provRow = (row.provenance && typeof row.provenance === 'object'
    ? row.provenance : {}) as Record<string, unknown>
  const chosen = (value: unknown): WorkspaceProvenance['modeSource'] | undefined =>
    value === 'default' || value === 'requested' || value === 'refused' ? value : undefined
  const provenance: WorkspaceProvenance = {
    source: provRow.source === 'declared' ? 'declared' : 'platform',
    ...(chosen(provRow.mode_source) ? { modeSource: chosen(provRow.mode_source) } : {}),
    ...(chosen(provRow.layout_source) ? { layoutSource: chosen(provRow.layout_source) } : {}),
  }

  const graphRow = (row.graph && typeof row.graph === 'object' && !Array.isArray(row.graph)
    ? row.graph : {}) as Record<string, unknown>

  // ── the layouts this host can actually place ────────────────────────────
  const offeredLayouts = asLayouts(graphRow.layouts)
  const unsupported = offeredLayouts.filter((l) => !supportedSet.has(l))
  const drawable = offeredLayouts.filter((l) => supportedSet.has(l))
  for (const l of unsupported) issues.push(`the ${l} layout has no renderer in this host`)

  const asDrawable = (value: unknown): GraphLayout | null =>
    typeof value === 'string' && (drawable as string[]).includes(value) ? value as GraphLayout : null
  const defaultLayout = asDrawable(graphRow.defaultLayout) ?? drawable[0] ?? DEFAULT_WORKSPACE.graph.defaultLayout
  const layout = asDrawable(graphRow.layout) ?? defaultLayout

  // ── the modes, less any this host cannot honour ─────────────────────────
  const offeredModes = asModes(row.modes)
  // List is always there. It is the accessible fallback and the only mode that
  // can add a relationship, so a server answer without it is repaired rather
  // than obeyed — and an absent answer resolves to it.
  const modes: WorkspaceMode[] = offeredModes.includes('list')
    ? offeredModes
    : [...offeredModes, 'list']
  // A Graph this host cannot draw is not on offer, however the deployment is
  // configured. The control is the host saying "I cannot draw this".
  const honoured = modes.filter((m) => m !== 'graph' || drawable.length > 0)
  if (modes.includes('graph') && drawable.length === 0) {
    issues.push('no graph layout has a renderer in this host')
  }

  const asHonoured = (value: unknown): WorkspaceMode | null =>
    typeof value === 'string' && (honoured as string[]).includes(value) ? value as WorkspaceMode : null
  const defaultMode = asHonoured(row.defaultMode) ?? 'list'
  const mode = asHonoured(row.mode) ?? defaultMode

  const maximumDepth = asDepth(graphRow.maximumDepth) ?? 1
  const initialDepth = Math.min(asDepth(graphRow.initialDepth) ?? 1, maximumDepth)

  return {
    modes: honoured,
    defaultMode,
    mode,
    graph: {
      layouts: drawable,
      defaultLayout,
      layout,
      initialDepth,
      maximumDepth,
      groupBy: 'relationshipType',
      unsupported,
    },
    provenance,
    // ISSUES decide this, not the rung. An undeclared workspace resolving to the
    // ruling's own default is a governed answer, not a degradation — which is
    // exactly the reading `resolveSlotPresentation` takes of its `platform`
    // rung. Which rung answered is reported separately, in `provenance.source`,
    // because that is a different question and an authoring surface needs both.
    status: issues.length > 0 ? 'fallback' : 'resolved',
    issues,
  }
}

/**
 * A stored personal workspace value, coerced to something this vocabulary
 * admits. Unknown becomes `null`, which means "no personal choice" and
 * therefore inherits the governed default — coercing it to List instead would
 * let a corrupt stored string silently outrank an administrator's answer, which
 * is the whole point of having one.
 *
 * Note what `asGraphLayout` does NOT do: it does not read a stored per-slot
 * `metro` as this choice. The two are different keys read by different
 * resolvers, and the ruling forbids reinterpreting one as the other.
 */
export const asWorkspaceMode = (value: unknown): WorkspaceMode | null =>
  typeof value === 'string' && MODES.has(value) ? value as WorkspaceMode : null

export const asGraphLayout = (value: unknown): GraphLayout | null =>
  typeof value === 'string' && LAYOUTS.has(value) ? value as GraphLayout : null

/**
 * Narrow the server's whole-record answer: every slot, each on its own terms.
 *
 * The `order` the server answered in is kept rather than the object key order,
 * because a record page renders its sections in a governed order and reading it
 * back off `Object.keys` would make that order an accident of serialisation.
 */
export function resolveRelatedPageEnvelope(
  envelope: unknown,
  supported: readonly RelatedPresentation[] = RELATED_PRESENTATIONS,
  supportedLayouts: readonly GraphLayout[] = GRAPH_LAYOUTS,
): HostResolvedRelatedPage {
  const row = (envelope && typeof envelope === 'object' ? envelope : {}) as Record<string, unknown>
  const cells = (row.slots && typeof row.slots === 'object' && !Array.isArray(row.slots)
    ? row.slots : {}) as Record<string, unknown>

  const slots: Record<string, HostResolvedSlotPresentation> = {}
  for (const [id, cell] of Object.entries(cells)) slots[id] = resolveSlotPresentation(id, cell, supported)

  const order = Object.entries(cells)
    .map(([id, cell]) => {
      const c = (cell && typeof cell === 'object' ? cell : {}) as Record<string, unknown>
      return { id, at: typeof c.order === 'number' ? c.order : Number.MAX_SAFE_INTEGER }
    })
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id))
    .map((s) => s.id)

  const provRow = (row.provenance && typeof row.provenance === 'object'
    ? row.provenance : {}) as Record<string, unknown>

  const workspace = resolveWorkspaceEnvelope(row.workspace, supportedLayouts)

  return {
    entity: named(row.entity) ? row.entity : '',
    slots,
    order,
    workspace,
    provenance: provRow.source ? { source: asProvenance(provRow.source) } : {},
    // About the SLOTS, deliberately, and unchanged by #2324. Folding the
    // workspace into this aggregate would change what the sentence means for
    // every existing reader — an old server that answers no workspace member at
    // all would make every page report `fallback` about its slots, which are
    // fine. The workspace reports its own status beside this one (rule 32).
    status: order.some((id) => slots[id]!.status === 'fallback') ? 'fallback' : 'resolved',
  }
}

/**
 * The stored personal value, coerced to a lens this vocabulary admits.
 *
 * `list` is the legacy value and still means the dense Grid — it meant the
 * relationship TABLE before its narrow-container CSS silently turned it into
 * cards, so it migrates to the truthful choice rather than becoming a new card
 * preference. Anything unknown becomes `null`, which means "no personal choice"
 * and therefore inherits the governed default: coercing it to Grid instead would
 * make a corrupt stored value silently outrank an administrator's default.
 */
/** A recipe on the wire, or null — never a half-read one. */
export function asPeekRecipe(value: unknown): PeekRecipe | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return validatePeekRecipe(value).length === 0 ? value as PeekRecipe : null
}

export function asRelatedPresentation(value: unknown): RelatedPresentation | null {
  if (value === 'list') return 'grid'
  return typeof value === 'string' && RELATED.has(value) ? value as RelatedPresentation : null
}

/**
 * A recipe from the wire, or null.
 *
 * A HALF recipe — one axis named and not the other — is null rather than a
 * partially-usable object, because a pin needs both and the honest answer to
 * "where is this record" is then "nowhere I can draw".
 */
export const asMapRecipe = (value: unknown): MapRecipe | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const row = value as Record<string, unknown>
  if (!named(row.lat) || !named(row.lng)) return null
  return {
    lat: row.lat, lng: row.lng,
    ...(named(row.title) ? { title: row.title } : {}),
    ...(named(row.subtitle) ? { subtitle: row.subtitle } : {}),
  }
}
